import { ChevronDown, Network as NetworkIcon, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useOrganization } from "@/client/backend/organization";
import { type TunnelRow, useDeleteOrganizationTunnel, useOrganizationTunnels } from "@/client/backend/tunnels";
import { AppShell, EmptyState, PageHeader } from "@/components/layout/AppShell";
import { NewTunnelDialog } from "@/components/network/new-tunnel-dialog";
import { TunnelToken } from "@/components/network/tunnel-token";
import { TunnelSelectors } from "@/components/network/tunnel-selectors";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useOrganizationScopes } from "@/lib/scopes";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/utils/format";

// A status line reads better with a coarse age; the exact time is in the
// tooltip.
function age(from: string): string {
	const minutes = Math.floor((Date.now() - new Date(from).getTime()) / 60_000);
	if (minutes < 1) return "under a minute";
	if (minutes < 60) return `${minutes}m`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h`;
	return `${Math.floor(hours / 24)}d`;
}

function TunnelStatus({ tunnel }: { tunnel: TunnelRow }) {
	const at = tunnel.online ? tunnel.connectedAt : tunnel.lastSeenAt;
	let text = tunnel.online ? "Online" : "Waiting for its connector";
	if (at) {
		text = tunnel.online ? `Online for ${age(at)}` : `Offline, last seen ${age(at)} ago`;
	}
	return (
		<span
			className={cn("inline-flex items-start gap-2 text-xs", tunnel.online ? "text-success" : "text-muted-foreground")}
			title={at ? formatDateTime(at) : undefined}
		>
			<span aria-hidden className="mt-[5px] size-1.5 shrink-0 rounded-full bg-current" />
			{text}
		</span>
	);
}

function TunnelInstances({ tunnel }: { tunnel: TunnelRow }) {
	const count = tunnel.instances.length;
	if (count === 0) {
		return <span className="text-muted-foreground">None</span>;
	}
	return (
		<Popover>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="inline-flex items-center gap-1 text-primary underline decoration-muted-foreground/50 underline-offset-4 hover:decoration-current"
				>
					{count === 1 ? "1 instance" : `${count} instances`}
					<ChevronDown aria-hidden className="size-3.5" />
				</button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-[28rem] max-w-[calc(100vw-2rem)] p-1">
				<ul>
					{tunnel.instances.map((instance) => (
						<li key={instance.id}>
							<Link
								to={`/stream/${instance.id}`}
								className="flex items-center justify-between gap-3 rounded-sm px-2 py-1.5 hover:bg-muted"
							>
								<span className="truncate font-mono text-xs">{instance.id}</span>
								<span className="shrink-0 text-xs text-muted-foreground">
									{instance.platform === "ios" ? "iOS" : "Android"}, {instance.state}
								</span>
							</Link>
						</li>
					))}
				</ul>
			</PopoverContent>
		</Popover>
	);
}

export function Network() {
	const [organization] = useOrganization();
	const organizationTid = organization !== "loading" ? organization.tid : undefined;
	const { can } = useOrganizationScopes(organization);
	const canManage = can("tunnel", "all");
	const { data, error, mutate } = useOrganizationTunnels(organizationTid);
	const { trigger: deleteTunnel, isMutating: deleting } = useDeleteOrganizationTunnel(organizationTid);
	const [pendingDelete, setPendingDelete] = useState<TunnelRow>();
	const [deleteError, setDeleteError] = useState<string>();
	// The page owns the dialog: the first tunnel replaces the empty state, which
	// would otherwise unmount the dialog before its token is copied.
	const [creating, setCreating] = useState(false);
	const tunnels = data?.data?.tunnels ?? [];
	const loaded = data?.data !== undefined;
	const loadError = error?.message ?? data?.error?.message;

	async function confirmDelete() {
		if (!pendingDelete) return;
		setDeleteError(undefined);
		const response = await deleteTunnel({ id: pendingDelete.id });
		if (response?.error) {
			setDeleteError(response.error.message);
		} else {
			await mutate();
		}
		setPendingDelete(undefined);
	}

	return (
		<AppShell title="Tunnels">
			<PageHeader
				title="Tunnels"
				description="Reach services in your private network from simulators and emulators."
			/>

			{loadError && (
				<div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-danger">
					Failed to load tunnels: {loadError}
				</div>
			)}
			{deleteError && (
				<div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-danger">
					Failed to delete the tunnel: {deleteError}
				</div>
			)}

			<section className="min-w-0 space-y-3">
				<div className="flex items-center justify-between gap-4">
					<h2 className="text-lg font-semibold tracking-tight">Tunnels</h2>
					{canManage && organizationTid && tunnels.length > 0 && (
						<Button size="sm" onClick={() => setCreating(true)}>
							New tunnel
						</Button>
					)}
				</div>

				<div className="min-w-0 border-t">
					{loadError ? null : !loaded ? (
						<div className="p-6 text-sm text-muted-foreground">Loading tunnels...</div>
					) : tunnels.length === 0 ? (
						<EmptyState
							icon={<NetworkIcon />}
							title="No tunnels yet"
							description="A tunnel runs on a machine in your network and serves every instance that names it."
						>
							{canManage && organizationTid ? (
								<Button size="sm" onClick={() => setCreating(true)}>
									New tunnel
								</Button>
							) : (
								<p className="text-sm text-muted-foreground">An admin of the organization creates tunnels.</p>
							)}
						</EmptyState>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Tunnel</TableHead>
									<TableHead className="hidden md:table-cell">Connector</TableHead>
									<TableHead>Selectors</TableHead>
									<TableHead className="hidden lg:table-cell">Token</TableHead>
									<TableHead>Instances</TableHead>
									<TableHead className="w-12">
										<span className="sr-only">Actions</span>
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{tunnels.map((tunnel) => (
									<TableRow key={tunnel.id} className={cn("group", !tunnel.online && "text-muted-foreground")}>
										<TableCell className="min-w-40 max-w-64 align-top">
											<div className="flex items-center gap-2">
												<span className="truncate font-semibold text-foreground" title={tunnel.name}>
													{tunnel.name}
												</span>
												{tunnel.ephemeral && (
													<Badge variant="outline" title="Goes away when its connector exits">
														Quick
													</Badge>
												)}
											</div>
											<TunnelStatus tunnel={tunnel} />
										</TableCell>
										<TableCell className="hidden align-top md:table-cell">
											{tunnel.hostname ? (
												<>
													<div className="truncate">{tunnel.hostname}</div>
													<div className="flex gap-3 text-xs text-muted-foreground">
														{tunnel.clientIp && <span>{tunnel.clientIp}</span>}
														{tunnel.clientVersion && <span>v{tunnel.clientVersion}</span>}
													</div>
												</>
											) : (
												<span className="text-muted-foreground">Not started</span>
											)}
										</TableCell>
										<TableCell className="align-top">
											{organizationTid && (
												<TunnelSelectors organizationId={organizationTid} tunnel={tunnel} canEdit={canManage} />
											)}
										</TableCell>
										<TableCell className="hidden align-top lg:table-cell">
											{organizationTid && !tunnel.ephemeral && (
												<TunnelToken organizationId={organizationTid} tunnel={tunnel} canManage={canManage} />
											)}
										</TableCell>
										<TableCell className="align-top">
											<TunnelInstances tunnel={tunnel} />
										</TableCell>
										<TableCell className="align-top">
											{canManage && (
												<Button
													variant="ghost"
													size="icon"
													className="-my-1.5 size-8 text-muted-foreground hover:text-danger"
													onClick={() => setPendingDelete(tunnel)}
												>
													<Trash2 className="size-4" />
													<span className="sr-only">Delete {tunnel.name}</span>
												</Button>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</section>

			{canManage && organizationTid && (
				<NewTunnelDialog organizationId={organizationTid} open={creating} onOpenChange={setCreating} />
			)}

			<AlertDialog
				open={pendingDelete !== undefined}
				onOpenChange={(open) => {
					if (!open) setPendingDelete(undefined);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete tunnel {pendingDelete?.name}?</AlertDialogTitle>
						<AlertDialogDescription>
							Its connector disconnects and exits, its token stops working, and instances on it lose access to your
							network.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							className={buttonVariants({ variant: "destructive" })}
							disabled={deleting}
							onClick={(event) => {
								// Keep the dialog open until the delete answers.
								event.preventDefault();
								void confirmDelete();
							}}
						>
							Delete tunnel
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</AppShell>
	);
}
